import SwapProcessing from './SwapProcessing';
import { UseSwapDataReturn } from '@/application/read/swap/useSwapData';
import { TransactionState, useNetwork } from '@nftx/react';
import { SwapState } from '@/domain/swap';
import { SwapPrice } from '@/core/swap';
import { Chain } from 'viem';
import { BadRequestError } from 'nftx.js';
import Icon, { Icons } from '@/presentation/components/elements/Icon';
import { fetchBuyAmount, fetchSellAmount } from '@/infrastructure/swap';
import { useSlippagePercentage } from '@/presentation/context/settings';
import useAddress from '@/application/read/web3/useAddress';
import { usePermit2 } from '@/presentation/context/permit2';

const SwapProcessingContainer = ({
  data,
  swapState,
  onCancel,
  onComplete,
  processTrade,
  error,
  status,
  txHash,
  chain,
}: {
  processTrade: ({ quote }: { quote: SwapPrice }) => Promise<any>;
  error: any;
  status: TransactionState;
  data: UseSwapDataReturn;
  swapState: SwapState;
  onCancel: () => void;
  onComplete: () => void;
  txHash?: `0x${string}` | undefined;
  chain: Chain;
}) => {
  const network = useNetwork();
  const { buyItems, sellItems } = swapState;
  const { quote, sellSymbol, buySymbol, buyAmount, sellAmount } = data;
  const slippagePercentage = useSlippagePercentage();
  const userAddress = useAddress();
  const [{ permit2Signature }] = usePermit2();

  const action = async () => {
    // We need to re-get a quote using the permit2 signature data
    // If we don't do this, the original quote is made without any permit2 data
    // and we don't have permission to do the swap
    const quote2 = await (() => {
      // If we don't have a permit2 signature (i.e. we're selling ETH or tokens that don't use permit2) we can just use the existing quote
      if (!permit2Signature) {
        return quote;
      }
      if (swapState.amountController === 'buy') {
        return fetchSellAmount({
          buyType: swapState.buyType,
          buyAmount: data.buyAmount,
          buyToken: swapState.buyToken || '0x',
          buyTokenIds: swapState.buyItems,
          buyVaultId: swapState.buyVaultId,
          sellType: swapState.sellType,
          sellTokenIds: swapState.sellItems,
          sellToken: swapState.sellToken || '0x',
          network,
          slippagePercentage,
          userAddress,
          permit2: permit2Signature,
        });
      }
      return fetchBuyAmount({
        buyType: swapState.buyType,
        buyToken: swapState.buyToken || '0x',
        buyTokenIds: swapState.buyItems,
        sellType: swapState.sellType,
        sellToken: swapState.sellToken || '0x',
        sellAmount: data.sellAmount,
        sellVaultId: swapState.sellVaultId,
        sellTokenIds: swapState.sellItems,
        slippagePercentage,
        network,
        userAddress,
        permit2: permit2Signature,
      });
    })();
    if (quote2 == null) {
      throw new BadRequestError('Cannot process transaction as quote is null');
    }
    return processTrade({ quote: quote2 });
  };

  if (!quote) {
    return (
      <div className="p-6 bg-gray-50 w-full flex justify-center items-center">
        <Icon name={Icons.SPINNER_GAP} className="animate-spin" />
      </div>
    );
  }

  return (
    <SwapProcessing
      network={network}
      action={action}
      actionError={error}
      actionStatus={status}
      onCancel={onCancel}
      onComplete={onComplete}
      quote={quote}
      sellSymbol={sellSymbol}
      buySymbol={buySymbol}
      buyAmount={buyAmount}
      sellAmount={sellAmount}
      buyItems={buyItems}
      sellItems={buyItems}
      txHash={txHash}
      chain={chain}
    />
  );
};

export default SwapProcessingContainer;
